import { Badge } from "@/components/ui/badge";
import {
  formatUsdValue,
  parseUsdValue,
  type ValuationStatus,
} from "@/features/valuation/valuation";

/** Accessible description for a cell that has no usable number (J4 / ALL-5643). */
export const VALUE_UNAVAILABLE_LABEL = "Valuation unavailable";

const STATUS_META: Record<ValuationStatus, { label: string; variant: "success" | "danger" | "pending" | "neutral" }> = {
  VALUED: { label: "Valued", variant: "success" },
  PARTIAL: { label: "Partial", variant: "pending" },
  UNVALUED: { label: "Unvalued", variant: "neutral" },
  STALE: { label: "Stale", variant: "pending" },
  UNAVAILABLE: { label: "Unavailable", variant: "danger" },
  UNKNOWN: { label: "Unknown", variant: "neutral" },
};

export function ValueCell({
  value,
  status = value === null || value === undefined ? "UNVALUED" : "VALUED",
  compact = false,
}: {
  value: string | number | null | undefined;
  status?: ValuationStatus;
  compact?: boolean;
}) {
  const meta = STATUS_META[status] ?? STATUS_META.UNKNOWN;
  // A cell is unvalued when there is no usable number, whatever the status
  // claims: a dash alone is not readable aloud, so it carries a label too.
  const unvalued = parseUsdValue(value) === null;
  return (
    <div className="flex flex-col items-start gap-1">
      <span className="font-mono tabular-nums text-[13px] text-ink">
        {formatUsdValue(value)}
      </span>
      {unvalued && (
        <span className="sr-only" data-testid="value-cell-unavailable-label">
          {VALUE_UNAVAILABLE_LABEL}
        </span>
      )}
      {!compact && status !== "VALUED" && <Badge variant={meta.variant} dot={false}>{meta.label}</Badge>}
    </div>
  );
}
